import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import S from './Widget.module.scss';

const WidgetRapports = ({ rapports = [] }) => {
  const latestRapports = rapports.slice(0, 10);
  const hasRapports = latestRapports && latestRapports.length > 0;
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setHasOverflow(maxScroll > 1);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    if (!hasRapports) return;
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => updateScrollState();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [hasRapports, latestRapports.length, updateScrollState]);

  const getScrollAmount = () => {
    const el = scrollRef.current;
    if (!el) return 240;
    const card = el.querySelector('[data-rapport-card]');
    const styles = window.getComputedStyle(el);
    const gapValue = parseFloat(styles.columnGap || styles.gap || '0');
    const cardWidth = card
      ? card.getBoundingClientRect().width
      : el.clientWidth;
    return cardWidth + (Number.isFinite(gapValue) ? gapValue : 0);
  };

  const handleScrollBy = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = getScrollAmount();
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <div className={[S.widget, S.widgetMedium, S.widgetSimple].join(' ')}>
      <div className={S.simpleHeader}>
        <h3 className={S.simpleTitle}>{checkMot('mes_rapports')}</h3>
      </div>
      <div className={S.simpleContent}>
        {hasRapports ? (
          <div className={S.rapportListWrapper}>
            {hasOverflow && (
              <>
                <button
                  type="button"
                  className={[
                    S.scrollButton,
                    S.scrollButtonLeft,
                    !canScrollLeft ? S.scrollButtonDisabled : '',
                  ].join(' ')}
                  onClick={() => handleScrollBy(-1)}
                  aria-label="Deslizar os relatórios para a esquerda"
                  disabled={!canScrollLeft}
                >
                  <FiChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  className={[
                    S.scrollButton,
                    S.scrollButtonRight,
                    !canScrollRight ? S.scrollButtonDisabled : '',
                  ].join(' ')}
                  onClick={() => handleScrollBy(1)}
                  aria-label="Deslizar os relatórios para a direita"
                  disabled={!canScrollRight}
                >
                  <FiChevronRight size={16} />
                </button>
              </>
            )}
            <div className={S.rapportCarousel}>
              <ul className={S.rapportList} ref={scrollRef}>
                {latestRapports.map((rapport, index) => {
                  const key = rapport.report_id || rapport.rapport_id || index;
                  const name =
                    rapport.template_name ||
                    rapport.report_name ||
                    rapport.titre ||
                    rapport.title ||
                    rapport.name ||
                    checkMot('rapport');
                  const epochSeconds =
                    rapport.generate_report_time ||
                    rapport.date ||
                    rapport.timestamp ||
                    null;
                  const dateLabel = epochSeconds
                    ? new Date(epochSeconds * 1000).toLocaleDateString()
                    : '';
                  const downloadPath =
                    rapport.path_report ||
                    rapport.report_path ||
                    rapport.file_path ||
                    null;
                  return (
                    <li key={key} className={S.rapportItem} data-rapport-card>
                      <span className={S.rapportIcon}>📄</span>
                      <span className={S.rapportName}>
                        {downloadPath ? (
                          <a
                            href={downloadPath}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className={S.rapportLink}
                          >
                            {name}
                          </a>
                        ) : (
                          name
                        )}
                      </span>
                      <span className={S.rapportDate}>{dateLabel}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : (
          <div className={S.emptyStateSimple}>
            <span className={S.placeholder}>{checkMot('mes_rapports')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetRapports;
